import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { useProduct, useProducts } from "@/api/hooks/use-products";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { formatPrice } from "@/lib/utils";

/**
 * Подбор товара из каталога по названию, артикулу или бренду.
 *
 * До этого товар в идее задавался вводом идентификатора руками: собрать образ из
 * товаров разных магазинов было нельзя без выписанных заранее ID. Теперь поиск
 * идёт по всему каталогу — площадка собирает коллаборации из чужих карточек.
 */
export function ProductPicker(t0) {
  const $ = _c(44);
  const {
    value,
    onChange,
    placeholder
  } = t0;
  const {
    t
  } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== query) {
    t1 = query.trim();
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const search = t1;
  const t2 = search || undefined;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      status: "all",
      page: 1,
      pageSize: 12,
      search: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const results = useProducts(t3);
  const selected = useProduct(value);
  if (value) {
    const p = selected.data;
    let t4;
    if ($[4] !== p) {
      t4 = p?.photos[0] && <img src={p.photos[0]} alt="" className="size-full object-cover" />;
      $[4] = p;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t4) {
      t5 = <div className="size-9 shrink-0 overflow-hidden rounded bg-muted">{t4}</div>;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    const t6 = p?.name ?? value;
    let t7;
    if ($[8] !== t6) {
      t7 = <div className="truncate text-sm">{t6}</div>;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    const t8 = p ? `${p.brand} · ${formatPrice(p.price)}` : value;
    let t9;
    if ($[10] !== t8) {
      t9 = <div className="truncate text-xs text-muted-foreground">{t8}</div>;
      $[10] = t8;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    let t10;
    if ($[12] !== t7 || $[13] !== t9) {
      t10 = <div className="min-w-0 flex-1">{t7}{t9}</div>;
      $[12] = t7;
      $[13] = t9;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== t) {
      t11 = t("ideas.createDialog.pinClear", {
        defaultValue: "\u0423\u0431\u0440\u0430\u0442\u044C \u0442\u043E\u0432\u0430\u0440"
      });
      $[15] = t;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== onChange) {
      t12 = () => {
        onChange("");
        setOpen(true);
      };
      $[17] = onChange;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    let t13;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <X className="size-4" />;
      $[19] = t13;
    } else {
      t13 = $[19];
    }
    let t14;
    if ($[20] !== t11 || $[21] !== t12) {
      t14 = <Button type="button" size="icon-sm" variant="ghost" aria-label={t11} onClick={t12}>{t13}</Button>;
      $[20] = t11;
      $[21] = t12;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] !== t10 || $[24] !== t14 || $[25] !== t5) {
      t15 = <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border px-2 py-1.5">{t5}{t10}{t14}</div>;
      $[23] = t10;
      $[24] = t14;
      $[25] = t5;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    return t15;
  }
  let t4;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Search className="size-4 shrink-0 text-muted-foreground" />;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  let t6;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setOpen(true);
    t6 = e => {
      setQuery(e.target.value);
      setOpen(true);
    };
    $[28] = t5;
    $[29] = t6;
  } else {
    t5 = $[28];
    t6 = $[29];
  }
  let t7;
  if ($[30] !== placeholder || $[31] !== t) {
    t7 = placeholder ?? t("ideas.createDialog.pinSearch", {
      defaultValue: "\u041D\u0430\u0439\u0442\u0438 \u0442\u043E\u0432\u0430\u0440 \u0432 \u043A\u0430\u0442\u0430\u043B\u043E\u0433\u0435"
    });
    $[30] = placeholder;
    $[31] = t;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== query || $[34] !== t7) {
    t8 = <div className="flex items-center gap-2">{t4}<Input value={query} onFocus={t5} onChange={t6} placeholder={t7} /></div>;
    $[33] = query;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== onChange || $[37] !== open || $[38] !== results || $[39] !== t) {
    t9 = open && <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-md">{results.isPending && <p className="p-2 text-xs text-muted-foreground">{t("products.loading", {
          defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026"
        })}</p>}{results.data?.data.length === 0 && <p className="p-2 text-xs text-muted-foreground">{t("products.empty", {
          defaultValue: "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E"
        })}</p>}{results.data?.data.map(p_0 => <button key={p_0.id} type="button" className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted" onClick={() => {
        onChange(p_0.id);
        setOpen(false);
        setQuery("");
      }}><div className="size-8 shrink-0 overflow-hidden rounded bg-muted">{p_0.photos[0] && <img src={p_0.photos[0]} alt="" className="size-full object-cover" />}</div><span className="min-w-0 flex-1"><span className="block truncate text-sm">{p_0.name}</span><span className="block truncate text-xs text-muted-foreground">{p_0.brand} · {formatPrice(p_0.price)}</span></span></button>)}</div>;
    $[36] = onChange;
    $[37] = open;
    $[38] = results;
    $[39] = t;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== t8 || $[42] !== t9) {
    t10 = <div className="relative min-w-0 flex-1">{t8}{t9}</div>;
    $[41] = t8;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  return t10;
}